/**
 * Wann die Vorschlags-Kachel („Soll ich noch etwas uebernehmen?") erscheinen darf.
 *
 * K2 (Antoine, 01.08.2026): „nervt etwas, wenn es mitten in der Arbeit, nach jeder Chat
 * Nachricht kommt." Die Kachel hing nur an `!busy && suggestions.length > 0` — sie erschien
 * nach JEDER Antwort, auch unter einer offenen Rueckfrage des Assistenten (sein Screenshot:
 * unter „Welche der 7 Anfragen interessiert dich?" stand die Kachel — zwei konkurrierende
 * Fragen uebereinander).
 *
 * Die Bedingung, in Worten: Vorschlaege sind ein Angebot fuer den LEERLAUF. Sie erscheinen,
 * wenn gerade nichts im Fluss ist — und je Unterhaltung nur einmal, solange sie dieselben sind.
 * Aendern sie sich (neue Lage), duerfen sie wieder erscheinen: Das ist Information, keine
 * Wiederholung.
 *
 * AUSDRUECKLICH KEINE Zaehl-Regel („nach 3x ausblenden") — Antoine hat hardcodierte Schwellen
 * abgelehnt. Ob das System aus wiederholtem Ignorieren LERNT, den Vorschlag ganz wegzulassen,
 * ist Sache des Gedaechtnis-Konzepts (KONZEPT-GEDAECHTNIS.md § 1b) und laeuft ueber beobachtete
 * Fakten, nicht ueber eine Schwelle in dieser Datei.
 *
 * Reine Funktion ohne React/DOM — pruefbar in Millisekunden (Muster: hooks/ladezustand.ts).
 */

export interface VorschlagsLage {
  /** Der Assistent arbeitet gerade. */
  busy: boolean;
  /** Eine Freigabe-Kachel wartet — der Betreiber ist mitten in einer Entscheidung. */
  hatOffeneFreigabe: boolean;
  /** Der letzte sichtbare Assistent-Text (leer, wenn es keinen gibt). */
  letzterAssistentText: string;
  /** Signatur der aktuellen Vorschlaege (leer = keine Vorschlaege). */
  signatur: string;
  /** Signatur, mit der die Kachel in dieser Unterhaltung zuletzt gezeigt wurde. */
  zuletztGezeigt: string | null;
  /** Der Betreiber arbeitet gerade an einem eigenen Punkt (siehe `amThemaDran`). */
  amThemaDran?: boolean;
}

/** Fragen nach dem Gesamtbild — darunter passen die allgemeinen Vorschlaege. */
const UEBERBLICK = ["was steht an?", "gib mir einen kurzen tagesüberblick."];

/**
 * Antoine, 12.10.2026: „Ich will keine Fragen im Chat sehen, die nichts mit dem aktuellen Punkt
 * zu tun haben." Hat der Betreiber selbst ein Thema angefangen (letzte eigene Nachricht ist keine
 * Ueberblicks-Frage), sind Mahnungen zu anderen Vorgaengen Ablenkung, kein Angebot.
 */
export function amThemaDran(letzteNutzerNachricht: string | null): boolean {
  if (letzteNutzerNachricht == null) return false;
  return !UEBERBLICK.includes(letzteNutzerNachricht.trim().toLowerCase());
}

export function zeigeVorschlaege(lage: VorschlagsLage): boolean {
  if (lage.busy) return false;
  if (lage.signatur === "") return false;
  if (lage.amThemaDran) return false;
  // Mitten in einer Entscheidung des Betreibers draengt sich nichts dazwischen.
  if (lage.hatOffeneFreigabe) return false;
  // Hat der Assistent gerade selbst eine Frage gestellt, gehoert die Buehne dieser Frage.
  if (lage.letzterAssistentText.trim().endsWith("?")) return false;
  // Dieselben Vorschlaege in derselben Unterhaltung nur einmal.
  if (lage.zuletztGezeigt === lage.signatur) return false;
  return true;
}

/**
 * Stabile Kennung der aktuellen Vorschlaege. Sortiert, damit die Reihenfolge der Quelle keine
 * Rolle spielt — sonst wuerde ein Umsortieren im Task-Board als „neue Vorschlaege" gelten.
 */
export function vorschlagsSignatur(vorschlaege: { kind: string; id: string }[]): string {
  return vorschlaege
    .map((v) => `${v.kind}:${v.id}`)
    .sort()
    .join("|");
}
